import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { generateAiDraft } from 'src/app/pages/startAPetitionV2/shared/api';
import { useImageJob } from 'src/app/pages/startAPetitionV2/shared/hooks';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { buildSchema } from './schema';

type SubmitInput = { issue: string; personalStory: string };

type UseSocialEntryReturn = ModelHookResult<
	{
		schema: ReturnType<typeof buildSchema>;
		defaultValues: { issue: string; personalStory: string };
		loading: boolean;
		error: Error | null;
	},
	{
		submit: (formData: { issue: string; personalStory: string }) => void;
		submitFromFooterCta: () => void;
		persistIssueDraft: (event: React.FocusEvent<HTMLInputElement>) => void;
		persistPersonalStoryDraft: (event: React.FocusEvent<HTMLInputElement>) => void;
		trackCtaSectionView: () => void;
	}
>;

/**
 * Hook for the Social Entry landing step. On submit, uses the draft input
 * to trigger AI draft generation and transition to preview.
 */
export function useSocialEntry(): UseSocialEntryReturn {
	const { data, actions } = useStartAPetitionPageState();
	const { translate } = useI18n();
	const track = useTracking();
	const trackOnce = useTrackOnce();

	const utilityContext = useUtilityContext();
	const { draft } = data;

	const { mutateAsync: createJobAsync } = useImageJob();

	const { mutate, error, ...mutationState } = useMutation({
		mutationFn: async (input: SubmitInput) => {
			const trimmed = {
				mainPrompt: input.issue.trim(),
				personalStory: input.personalStory.trim(),
			};

			const [aiDraftResponse, imageResponse] = await Promise.allSettled([
				generateAiDraft(trimmed, utilityContext),
				createJobAsync(trimmed),
			]);

			if (aiDraftResponse.status === 'rejected') throw aiDraftResponse.reason;

			const image =
				imageResponse.status === 'fulfilled' ? { image: { responseId: imageResponse.value.jobId } } : undefined;

			return { ...aiDraftResponse.value, ...image, input: trimmed };
		},
		onMutate: (input) => {
			void track('sap_social_entry_submit', {
				prompt: input.issue,
				personal_story: input.personalStory,
			});
		},
		onSuccess: (response) => {
			const recommendedDraft = { ask: response.ask, description: response.description, image: null };
			const metadata = { imageJobId: response.image?.responseId ?? null, imageGenerations: response.image ? 1 : 0 };
			actions.send({
				type: 'submit',
				draft: {
					input: { issuePrompt: response.input.mainPrompt, personalStory: response.input.personalStory },
					recommendedDraft,
					petitionDraft: recommendedDraft,
					metadata,
				},
			});
		},
		onError: (e) => {
			void track('sap_social_entry_submit_error', { error: e.constructor.name });
		},
	});

	const getDraftInput = useCallback(
		(): SubmitInput => ({
			issue: draft?.input?.issuePrompt ?? '',
			personalStory: draft?.input?.personalStory ?? '',
		}),
		[draft],
	);

	const submit = useCallback(
		(formData: { issue: string; personalStory: string }) => {
			void track('sap_social_entry_main_cta_click');
			mutate({ issue: formData.issue, personalStory: formData.personalStory });
		},
		[mutate, track],
	);

	const submitFromFooterCta = useCallback(() => {
		const input = getDraftInput();
		if (!input.issue.trim()) return;
		void track('sap_social_entry_footer_cta_click');
		mutate(input);
	}, [mutate, track, getDraftInput]);

	const persistIssueDraft = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			actions.send({ type: 'submit', draft: { input: { issuePrompt: event.target.value.trim() } }, target: 'self' });
		},
		[actions],
	);

	const persistPersonalStoryDraft = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			actions.send({ type: 'submit', draft: { input: { personalStory: event.target.value.trim() } }, target: 'self' });
		},
		[actions],
	);

	const trackCtaSectionView = useCallback(() => {
		void trackOnce('sap_social_entry_cta_footer_section_view');
	}, [trackOnce]);

	return {
		data: {
			schema: buildSchema({ translate }),
			defaultValues: {
				issue: draft?.input?.issuePrompt ?? '',
				personalStory: draft?.input?.personalStory ?? '',
			},
			loading: isLoading(mutationState) || isLoaded(mutationState),
			error,
		},
		actions: { submit, submitFromFooterCta, persistIssueDraft, persistPersonalStoryDraft, trackCtaSectionView },
	};
}
